<script lang="ts">
    // "Sign In" form.
    import { onDestroy, onMount } from 'svelte'
    import { writable, type Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import * as yup from 'yup'

    // import { gettext as _ } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { session } from 'hydria/services/session'

    // Clear the error after some time
    const CLEAR_ERROR_AFTER_MS = 7500

    // Bunch of functions to call on destroy, like store subscriptions.
    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface Props {
        goto: (loc: string) => void
    }

    let { goto }: Props = $props()

    // If not null, we need a 2-fa code to proceed
    const otpType: Writable<null | 'recovery' | 'sms' | 'totp' | 'email'> = writable(null)
    // If not null, this is the phone number we sms'ed, or email we emailed
    const otpNumber = writable(null)

    const passkeyRefId = writable('')
    const passkeyAuth = writable('')

    const schema = yup.object().shape({
        email: yup.string().email().required().label('Email'),
        password: yup.string().required().label('Password'),
        publicPlace: yup.boolean().label('I’m using an unsafe computer'),
        code: yup.string().when('$otpType', ([lvalue], schema) => {
            if (lvalue == 'recovery') {
                return schema.concat(
                    yup
                        .string()
                        .required()
                        .matches(/^[a-fA-F0-9]{5}-?[a-fA-F0-9]{5}$/),
                )
            }
            if (lvalue != null) {
                return schema.concat(
                    yup
                        .string()
                        .required()
                        .matches(/^[0-9]{6}$/),
                )
                // return schema.concat(yup.number().required().min(100000).max(999999))
            }
            return schema.optional()
        }),
    })

    interface IFields {
        email: string
        password: string
        publicPlace: boolean
        code?: string
    }

    const initialFields: IFields = { email: '', password: '', publicPlace: false, code: undefined }
    let fields: IFields = $state({ ...initialFields })

    // When true we are signing in, (e.g. show a spinner)
    let signinIn = writable(false)

    // When not null, there was an error to show.
    // That error is shown for a few seconds or until a value changes.
    const error = writable(null as null | string)
    let errorTimer: ReturnType<typeof setTimeout> | undefined = undefined

    function clearErrorTimer() {
        if (errorTimer) {
            clearTimeout(errorTimer)
            errorTimer = undefined
        }
    }
    function clearError() {
        if ($error != null) {
            clearErrorTimer()
            error.set(null)
        }
    }

    unsubs.push(
        error.subscribe((newError) => {
            // This is called when the `error` store changes
            clearErrorTimer()
            if (newError != null) {
                errorTimer = setTimeout(() => {
                    errorTimer = undefined
                    clearError()
                }, CLEAR_ERROR_AFTER_MS)
            }
        }),
    )

    const handleCodeChange = ({ detail }) => {
        fields.code = detail.inputState.unmaskedValue
        // fields.code = parseInt(detail.inputState.unmaskedValue, 10)
    }

    // Set 'isvalid' variables based on their validity when a value changes
    //@ts-expect-error
    let emailIsValid = $derived(schema.fields.email.isValidSync(fields.email))
    //@ts-expect-error
    let passwordIsValid = $derived(schema.fields.password.isValidSync(fields.password))
    let codeIsValid = $derived((schema.fields.code as yup.StringSchema).isValidSync(fields.code, { context: { otpNumber: $otpNumber } }))
    let formIsValid = $derived(schema.isValidSync(fields, { context: { otpType: $otpType } }))

    let emailDirtied = false
    let emailIsDirty = $derived.by(() => {
        if (fields.email != '') {
            if (!emailDirtied) emailDirtied = true
            return true
        }
        if (emailDirtied) return true
        return false
    })
    let passwordDirtied = false
    let passwordIsDirty = $derived.by(() => {
        if (fields.password != '') {
            if (!passwordDirtied) passwordDirtied = true
            return true
        }
        if (passwordDirtied) return true
        return false
    })

    interface OtpEntry {
        id: string
        type: 'sms' | 'totp' | 'email'
        code: string
        comment: string
    }

    let otpTypes: Set<'recovery' | 'sms' | 'totp' | 'email'> = $state(new Set())

    $effect(() => {
        fields
        if (errorTimer != null) clearError()
    })

    // Perform a signin
    async function formSubmit() {
        if (!formIsValid || $error) {
            return
        }
        signinIn.set(true)
        let res: any
        try {
            res = await session.signin(fields.email, fields.password, !fields.publicPlace, $otpType, $otpType != null ? fields.code : undefined)
        } catch (e) {
            res = e
        }
        return handleFormResult(res)
    }

    function handleFormResult(res: any) {
        if (!res.signedIn && res.ecode == 'CODE_RECENT') {
            error.set($lg == 'fr' ? 'Un code a récemment été envoyé' : 'A code has recently been sent')
        } else if (!res.signedIn && res.ecode == 'CODE_SPAM') {
            error.set($lg == 'fr' ? 'Trop de codes ont récemment été envoyés' : 'Too many codes have been sent recently')
        } else if (!res.signedIn && res.requireOtp) {
            otpType.set(res.requireOtp)
            otpNumber.set(res.otpNumber)
            otpTypes = new Set(res.altOtpMethods.map((m: OtpEntry) => m.type))
            if (res.recoveryPossible) {
                otpTypes.add('recovery')
            }
        } else if (!res.signedIn && res.codeInvalid) {
            error.set($lg == 'fr' ? 'Code invalide' : 'Invalid code')
        } else if (!res.signedIn) {
            if (res.error) error.set(res.error)
            else error.set($lg == 'fr' ? 'L’opération n’a pas pu être effectuée' : 'The operation could not be completed')
        }
        signinIn.set(false)
    }

    async function doPasskey() {
        const pkr = await session.passkeyPrepare()
        if (pkr != null) {
            let credentials
            try {
                credentials = await navigator.credentials.get({ publicKey: pkr.auth, mediation: 'conditional' })
            } catch (e) {
                if (e instanceof DOMException) {
                    console.warn('Public Key Credentials failed:', e.name)
                } else {
                    console.error('Public Key Error', e)
                }
                return
            }

            signinIn.set(true)
            let res: any
            try {
                res = await session.passkeySignin(!fields.publicPlace, pkr.refId, credentials)
            } catch (e) {
                res = e
            }
            return handleFormResult(res)
        }
    }

    async function changeTo(type: 'recovery' | 'sms' | 'email' | 'totp') {
        if (type === $otpType) return
        if (type == 'totp' || type == 'recovery') {
            $otpType = type
            return
        }

        signinIn.set(true)
        let res: any
        try {
            res = await session.signin(fields.email, fields.password, !fields.publicPlace, type, undefined, (type == 'email' && $otpType == 'sms') || (type == 'sms' && $otpType == 'email'))
        } catch (e) {
            res = e
        }
        return handleFormResult(res)
    }

    async function resendCode() {
        if ($otpType != 'email' && $otpType != 'sms') return
        signinIn.set(true)
        let res: any
        try {
            res = await session.signin(fields.email, fields.password, !fields.publicPlace, $otpType, undefined, true)
        } catch (e) {
            res = e
        }
        return handleFormResult(res)
    }

    function clearAll() {
        if ($signinIn) return
        clearError()
        otpType.set(null)
        otpNumber.set(null)
        fields = Object.assign(fields, initialFields)
    }

    let mainInput: HTMLElement | undefined = $state()
    let mounted = $state(false)
    onMount(() => {
        mounted = true
        // if (canAutofocus) mainInput.focus()

        if (window.PublicKeyCredential) {
            doPasskey()
        }
    })
    $effect(() => {
        if (mounted && mainInput) mainInput.focus()
    })
</script>

<div class="section-title">
    {'Administration'}
</div>

<form
    novalidate
    onsubmit={(e) => {
        e.preventDefault()
        formSubmit()
    }}
>
    <div class="inp-email">
        <label for="email" class="sr-only">{$lg == 'fr' ? 'Adresse électronique' : 'Email address'}</label>
        <div class="input-group">
            <span class="input-group-text"><Icon inline icon="fa:user" /></span>
            <input class="form-control" id="email" type="email" bind:value={fields.email} disabled={$signinIn || $otpType != null} bind:this={mainInput} oninput={() => clearError()} class:is-invalid={!emailIsValid && emailIsDirty} class:is-ok={emailIsValid} class:disabled={$signinIn} />
        </div>
    </div>
    <div class="inp-password">
        <label for="password" class="sr-only">{$lg == 'fr' ? 'Mot de passe' : 'Password'}</label>
        <div class="input-group" class:disabled={$signinIn}>
            <span class="input-group-text"><Icon inline icon="fa:lock" /></span>
            <input class="form-control" id="password" type="password" bind:value={fields.password} disabled={$signinIn || $otpType != null} oninput={() => clearError()} class:is-invalid={!passwordIsValid && passwordIsDirty} class:is-ok={passwordIsValid} />
        </div>
    </div>

    {#if $otpType != null}
        {#if $otpType == 'sms'}
            <div class="twofa-text">
                {#if $lg == 'fr'}
                    Un message texte avec 6 chiffres a été envoyé à {$otpNumber}.
                {:else}
                    A text message with a 6–digit verification code was sent to {$otpNumber}.
                {/if}
            </div>
        {/if}
        <div class="inp-twofa">
            <label for="twofa" class="sr-only">{$lg == 'fr' ? 'Code 2-fa' : '2-fa code'}</label>
            <div class="input-group">
                <div class="input-group-text"><Icon inline icon={$otpType == 'recovery' ? 'mdi:key' : 'fa:user-secret'} /></div>
                {#if $otpType == 'recovery'}
                    <input class="form-control" id="twofa" type="text" bind:value={fields.code} disabled={$signinIn} placeholder="XXXXX-XXXXX" oninput={() => clearError()} class:is-invalid={!codeIsValid} autocomplete="off" />
                {:else}
                    <input class="form-control" id="twofa" type="text" bind:value={fields.code} disabled={$signinIn} placeholder="XXXXXX" oninput={() => clearError()} class:is-invalid={!codeIsValid} autocomplete="one-time-code" />
                {/if}
                <button class="btn btn-outline-secondary" type="button" onclick={() => clearAll()} title={$lg == 'fr' ? 'Changer de compte' : 'Change to another account'} disabled={$signinIn} class:is-invalid={!codeIsValid} class:is-valid={codeIsValid}><Icon inline icon="fa:times" /></button>
            </div>
            {#if otpTypes.size > 1}
                <div class="twofa-actions">
                    {#if $otpType === 'sms' || $otpType === 'email'}
                        <div class="twofa-action">
                            <button
                                class="btn btn-outline-secondary btn-sm"
                                type="button"
                                disabled={$signinIn}
                                onclick={() => {
                                    resendCode()
                                }}><Icon inline icon="mdi:refresh" /> {$lg == 'fr' ? 'Nouveau code' : 'New code'}</button
                            >
                        </div>
                        {#if otpTypes.has('totp')}
                            <div class="twofa-action">
                                <button
                                    class="btn btn-outline-secondary btn-sm"
                                    type="button"
                                    disabled={$signinIn}
                                    onclick={() => {
                                        changeTo('totp')
                                    }}><Icon inline icon="mdi:cellphone" /> {$lg == 'fr' ? 'App' : 'App'}</button
                                >
                            </div>
                        {/if}
                    {/if}
                    {#if $otpType != 'sms' && otpTypes.has('sms')}
                        <div class="twofa-action">
                            <button
                                class="btn btn-outline-secondary btn-sm"
                                type="button"
                                disabled={$signinIn}
                                onclick={() => {
                                    changeTo('sms')
                                }}><Icon inline icon="mdi:message-text" /> {$lg == 'fr' ? 'SMS / Texto' : 'SMS / Text'}</button
                            >
                        </div>
                    {/if}
                    {#if $otpType != 'email' && otpTypes.has('email')}
                        <div class="twofa-action">
                            <button
                                class="btn btn-outline-secondary btn-sm"
                                type="button"
                                disabled={$signinIn}
                                onclick={() => {
                                    changeTo('email')
                                }}><Icon inline icon="mdi:email" /> {$lg == 'fr' ? 'Courriel' : 'Email'}</button
                            >
                        </div>
                    {/if}
                    {#if $otpType != 'recovery' && otpTypes.has('recovery')}
                        <div class="twofa-action">
                            <button
                                class="btn btn-outline-secondary btn-sm"
                                type="button"
                                disabled={$signinIn}
                                onclick={() => {
                                    changeTo('recovery')
                                }}><Icon inline icon="mdi:key" /> {$lg == 'fr' ? 'Code de récupération' : 'Recovery code'}</button
                            >
                        </div>
                    {/if}
                </div>
            {/if}
        </div>
    {/if}

    {#if $error != null}
        <div class="error">
            <div class="alert alert-danger">{$error}</div>
        </div>
    {:else}
        <div class="inp-action">
            <button class="btn btn-primary btn-block" type="submit" disabled={!formIsValid || $signinIn || $error != null}>
                {#if $signinIn}{$lg == 'fr' ? 'Veuillez patienter…' : 'Validating…'}
                {:else if $otpNumber}{$lg == 'fr' ? 'Valider' : 'Validate'}
                {:else}{$lg == 'fr' ? 'Se connecter' : 'Sign in'}
                {/if}
            </button>
        </div>
    {/if}

    <div class="extras">
        <div class="inp-public">
            <label for="publicPlace">
                <input id="publicPlace" type="checkbox" bind:checked={fields.publicPlace} disabled={$signinIn} oninput={() => clearError()} />
                {$lg == 'fr' ? 'Je suis dans un lieu public' : 'I’m in a public space'}
            </label>
        </div>
        {#if fields.publicPlace}
            <div class="inp-public-text">
                {#if $lg == 'fr'}
                    La session sera perdue dès la fermeture de la fenêtre ou de l’onglet.
                {:else}
                    The session will be lost when closing the browser’s window or the tab.
                {/if}
            </div>
        {/if}
        {#if !$otpType}
            <div class="lost-pw-link">
                <button class="btn btn-link btn-a" type="button" onclick={() => goto('lost-password')}>{$lg == 'fr' ? 'Mot de passe oublié ?' : 'Lost password?'}</button>
            </div>
        {/if}
    </div>
</form>
